import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import {
	Settings,
	Database,
	FileText,
	BarChart3,
	Shield,
	Mail,
	Trash2,
	Table,
} from "lucide-react";
import { Chip } from "@mui/material";
import { SupportToolCard } from "./components/support-tool-card.component";
import { useCentralStore } from "../../store/Central";
import { isDeveloper } from "@/lib/utils/is-app-user";

const supportTools = [
	{
		title: "Beträge Migrieren",
		description:
			"Aktualisiert fehlende Beträge für abgeschlossene Lieferscheine mit der Frontend-Berechnungslogik. Stellt Datenkonsistenz und korrekte Abrechnung sicher.",
		icon: <Database className="w-5 h-5 text-blue-600" />,
		path: "/support/migrate-amounts",
	},
	...(isDeveloper(import.meta.env)
		? [
				{
					title: "PDF Export",
					description:
						"Entwicklungs-Tool zum Generieren von Test-PDFs für verschiedene Organisationen. Erstellt ZIP-Dateien mit Beispiel-Dokumenten.",
					icon: <FileText className="w-5 h-5 text-purple-600" />,
					path: "/support/pdf-export",
				},
				{
					title: "PDF Diff",
					description:
						"Vergleicht zwei PDF-ZIP-Dateien pixelgenau und zeigt Unterschiede an. Nützlich zum Testen von PDF-Änderungen.",
					icon: <FileText className="w-5 h-5 text-orange-600" />,
					path: "/support/pdf-diff",
				},
				{
					title: "QR-Code Tester",
					description:
						"Testet die QR-Code Generierung für Zahlscheine mit benutzerdefinierten Eingabedaten. Zeigt Validierungsfehler und Rendering-Fehler an.",
					icon: <FileText className="w-5 h-5 text-green-600" />,
					path: "/support/qr-tester",
				},
				{
					title: "E-Mail Sender",
					description:
						"Versende E-Mail-Vorlagen für Tests. Unterstützt alle verfügbaren E-Mail-Templates wie Einladungen und Dateifreigaben.",
					icon: <Mail className="w-5 h-5 text-blue-600" />,
					path: "/support/email-sender",
				},
				{
					title: "Storage Cleanup",
					description:
						"Löscht Dateien aus Supabase Storage anhand von JSON-Eingaben. Ermöglicht das Bereinigen von verwaisten Dateien.",
					icon: <Trash2 className="w-5 h-5 text-red-600" />,
					path: "/support/storage-cleanup",
				},
				{
					title: "Debitoren Export",
					description:
						"Testet den PDF/CSV/Excel-Export für Debitoren mit Beispieldaten. Zeigt Vorschau und alle Export-Optionen.",
					icon: <Table className="w-5 h-5 text-teal-600" />,
					path: "/support/debitoren-export",
				},
				{
					title: "Blob zu PDF",
					description:
						"Rendert rohe Blob-Daten als PDF. Unterstützt Base64, Data-URLs, Bytea-Hex, Byte-Arrays und Buffer-JSON sowie Datei-Upload.",
					icon: <FileText className="w-5 h-5 text-indigo-600" />,
					path: "/support/blob-to-pdf",
				},
				{
					title: "DPI-Simulation",
					description:
						"Simuliert den Rasterdruck (Treiber-Kompatibilitätsmodus) auf verschiedenen DPI-Stufen im Vergleich zur Vektor-Referenz.",
					icon: <FileText className="w-5 h-5 text-pink-600" />,
					path: "/support/dpi-simulator",
				},
			]
		: []),
];

export const SupportPage = () => {
	const { profile } = useCentralStore((state) => ({
		profile: state.profile,
	}));

	// Check if user should have access to support tools
	const isSupportAccount =
		isDeveloper(import.meta.env, profile?.email) ||
		window.location.hostname === "localhost";

	if (!isSupportAccount) {
		return (
			<div className="flex flex-col px-12 py-6">
				<HeaderPortal>
					<PageTitle
						title={[{ name: "Zugriff verweigert" }]}
						icon={
							<Shield
								width="16px"
								height="16px"
								color="rgb(55, 65, 81)"
							/>
						}
					/>
				</HeaderPortal>
				<div className="flex flex-col items-center justify-center py-12">
					<Shield className="w-16 h-16 text-red-500 mb-4" />
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						Zugriff verweigert
					</h2>
					<p className="text-gray-600">
						Sie haben keine Berechtigung für diese Support-Tools.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[{ name: "Support" }]}
					icon={
						<Settings
							width="16px"
							height="16px"
							color="rgb(55, 65, 81)"
						/>
					}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-2xl font-bold text-gray-900">
								Support & Wartung
							</h1>
							<p className="text-gray-600 mt-1">
								Administrative Tools und Utilities für
								Systemwartung und Datenoperationen
							</p>
						</div>
						<Chip
							label={`${supportTools.length} Tools verfügbar`}
							color="primary"
							variant="outlined"
							icon={<BarChart3 className="w-4 h-4" />}
						/>
					</div>

					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<div className="flex items-start gap-3">
							<Shield className="w-5 h-5 text-blue-600 mt-0.5" />
							<div>
								<h3 className="font-medium text-blue-900">
									Wichtiger Hinweis
								</h3>
								<p className="text-blue-800 text-sm mt-1">
									Diese Tools sind für administrative Zwecke
									gedacht. Bitte verwenden Sie sie mit
									Vorsicht und erstellen Sie bei Bedarf
									Backups vor größeren Operationen.
								</p>
							</div>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
					{supportTools.map((tool, index) => (
						<SupportToolCard
							key={index}
							title={tool.title}
							description={tool.description}
							icon={tool.icon}
							path={tool.path}
						/>
					))}
				</div>

				{supportTools.length === 0 && (
					<div className="text-center py-12">
						<FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
						<h3 className="text-lg font-medium text-gray-900 mb-2">
							Keine Tools verfügbar
						</h3>
						<p className="text-gray-600">
							Derzeit sind keine Support-Tools konfiguriert.
						</p>
					</div>
				)}
			</div>
		</div>
	);
};
